if (!customElements.get('predictive-search')) {
  const MIN_QUERY_LENGTH = 2
  const DEBOUNCE_WAIT = 250

  function debounce (fn, wait) {
    let t
    return (...args) => {
      clearTimeout(t)
      t = setTimeout(() => fn.apply(this, args), wait)
    }
  }

  customElements.define(
    'predictive-search',
    class PredictiveSearch extends window.BAO.CustomElement {
      constructor () {
        super()

        this.sectionId = this.dataset.sectionId
        this.isOpen = false
        this.activeQuery = ''

        this.onInput = debounce(this.onInput.bind(this), DEBOUNCE_WAIT)
        this.onFocus = this.onFocus.bind(this)
        this.onKeydown = this.onKeydown.bind(this)
        this.onDocumentClick = this.onDocumentClick.bind(this)
        this.onPanelClick = this.onPanelClick.bind(this)
        this.close = this.close.bind(this)
      }

      connectedCallback () {
        document.body.appendChild(this.els.panel.element)

        super.connectedCallback()
      }

      setupListeners () {
        super.setupListeners()

        this.listeners.add(this.els.input.element, 'input', this.onInput)
        this.listeners.add(this.els.input.element, 'focus', this.onFocus)
        this.listeners.add(this, 'keydown', this.onKeydown)
        // The close button lives inside the fetched results, not the static shell, so it
        // isn't present in this.els at construction time - delegate instead.
        this.listeners.add(this.els.panel.element, 'click', this.onPanelClick)
        this.listeners.add(document, 'click', this.onDocumentClick)
      }

      onInput (event) {
        const query = event.target.value.trim()

        if (query.length < MIN_QUERY_LENGTH) {
          this.activeQuery = ''
          if (!this.showPopularSearches()) this.close()
          return
        }

        if (query === this.activeQuery) return

        this.fetchResults(query)
      }

      onFocus (event) {
        const query = event.target.value.trim()

        if (query.length < MIN_QUERY_LENGTH) this.showPopularSearches()
      }

      onKeydown (event) {
        if (event.code === 'Escape') {
          event.preventDefault()
          this.close()
          this.els.input.element.focus()
          return
        }

        if (!this.isOpen) return

        const results = this.els.resultLink?.elements || []
        if (results.length === 0) return

        if (event.code !== 'ArrowDown' && event.code !== 'ArrowUp') return

        event.preventDefault()

        const currentIndex = results.indexOf(document.activeElement)
        let nextIndex =
          event.code === 'ArrowDown' ? currentIndex + 1 : currentIndex - 1

        if (nextIndex >= results.length) nextIndex = 0
        if (nextIndex < 0) nextIndex = results.length - 1

        results[nextIndex].focus()
      }

      onDocumentClick (event) {
        if (this.contains(event.target)) return
        if (this.els.panel.element.contains(event.target)) return

        this.close()
      }

      onPanelClick (event) {
        if (!event.target.closest('[data-predictive-search-el="closeButton"]'))
          return

        this.close()
      }

      fetchResults (query) {
        this.activeQuery = query

        const url = new URL(
          window.routes.predictive_search_url,
          window.location.origin
        )
        url.searchParams.set('q', query)
        url.searchParams.set('section_id', this.sectionId)
        url.searchParams.set('resources[type]', 'product,collection,page,query')
        url.searchParams.set('resources[limit]', '8')
        url.searchParams.set('resources[limit_scope]', 'each')
        url.searchParams.set('resources[options][unavailable_products]', 'last')

        fetch(url.toString())
          .then(response => response.text())
          .then(html => {
            // A later keystroke may have already superseded this request.
            if (query !== this.activeQuery) return

            const doc = new DOMParser().parseFromString(html, 'text/html')
            const newResults = doc.querySelector('.ips-Results')

            if (!newResults) return

            this.els.results.element.innerHTML = newResults.innerHTML
            this.els.results.element.className = newResults.className

            this.syncResultLinks(this.els.results.element)
            this.showResults()
            this.open()
          })
          .catch(() => {
            this.close()
          })
      }

      showPopularSearches () {
        if (!this.els.popularSearches) return false

        this.els.results.element.hidden = true
        this.els.popularSearches.element.hidden = false
        this.syncResultLinks(this.els.popularSearches.element)
        this.open()
        return true
      }

      showResults () {
        if (this.els.popularSearches)
          this.els.popularSearches.element.hidden = true

        this.els.results.element.hidden = false
      }

      syncResultLinks (container) {
        const resultLinks = Array.from(
          container.querySelectorAll('[data-predictive-search-el="resultLink"]')
        )

        this.els.resultLink = {
          exists: resultLinks.length > 0,
          elements: resultLinks,
          element: resultLinks[0]
        }
      }

      open () {
        if (this.isOpen) return

        this.isOpen = true
        this.classList.add('is-open')
        document.body.classList.add(
          'is-PredictiveSearchOpen',
          'overflow-hidden'
        )
        this.els.panel.element.hidden = false
        this.els.input.element.setAttribute('aria-expanded', 'true')
      }

      close () {
        if (!this.isOpen) return

        this.isOpen = false
        this.classList.remove('is-open')
        document.body.classList.remove(
          'is-PredictiveSearchOpen',
          'overflow-hidden'
        )
        this.els.panel.element.hidden = true
        this.els.input.element.setAttribute('aria-expanded', 'false')
      }
    }
  )
}
